/**
 * Color schemes configuration for CoOrdio Cortex theme.
 * CoOrdio Design System with Cortex accent (#F43F5E).
 */

// Type Imports
import type { Skin } from '../@core/types';

/**
 * Color schemes type used by our theme system.
 */
export type ColorSchemes = {
  light: { palette: Record<string, any> };
  dark: { palette: Record<string, any> };
};

/**
 * Color schemes configuration function
 * @param skin - Skin type (default or bordered)
 * @returns Color schemes object
 */
const colorSchemes = (skin: Skin): ColorSchemes => {
  return {
    light: {
      palette: {
        primary: {
          main: '#F43F5E',
          light: '#FB7185',
          dark: '#E11D48',
          contrastText: '#FFF',
          lighterOpacity: 'rgb(var(--mui-palette-primary-mainChannel) / 0.08)',
          lightOpacity: 'rgb(var(--mui-palette-primary-mainChannel) / 0.16)',
          mainOpacity: 'rgb(var(--mui-palette-primary-mainChannel) / 0.24)',
          darkOpacity: 'rgb(var(--mui-palette-primary-mainChannel) / 0.32)',
          darkerOpacity: 'rgb(var(--mui-palette-primary-mainChannel) / 0.38)',
        },
        secondary: {
          main: '#94A3B8',
          light: '#CBD5E1',
          dark: '#64748B',
          contrastText: '#FFF',
          lighterOpacity: 'rgb(var(--mui-palette-secondary-mainChannel) / 0.08)',
          lightOpacity: 'rgb(var(--mui-palette-secondary-mainChannel) / 0.16)',
          mainOpacity: 'rgb(var(--mui-palette-secondary-mainChannel) / 0.24)',
          darkOpacity: 'rgb(var(--mui-palette-secondary-mainChannel) / 0.32)',
          darkerOpacity: 'rgb(var(--mui-palette-secondary-mainChannel) / 0.38)',
        },
        error: {
          main: '#EF4444',
          light: '#FCA5A5',
          dark: '#DC2626',
          contrastText: '#FFF',
          lighterOpacity: 'rgb(var(--mui-palette-error-mainChannel) / 0.08)',
          lightOpacity: 'rgb(var(--mui-palette-error-mainChannel) / 0.16)',
          mainOpacity: 'rgb(var(--mui-palette-error-mainChannel) / 0.24)',
          darkOpacity: 'rgb(var(--mui-palette-error-mainChannel) / 0.32)',
          darkerOpacity: 'rgb(var(--mui-palette-error-mainChannel) / 0.38)',
        },
        warning: {
          main: '#F59E0B',
          light: '#FCD34D',
          dark: '#D97706',
          contrastText: '#FFF',
          lighterOpacity: 'rgb(var(--mui-palette-warning-mainChannel) / 0.08)',
          lightOpacity: 'rgb(var(--mui-palette-warning-mainChannel) / 0.16)',
          mainOpacity: 'rgb(var(--mui-palette-warning-mainChannel) / 0.24)',
          darkOpacity: 'rgb(var(--mui-palette-warning-mainChannel) / 0.32)',
          darkerOpacity: 'rgb(var(--mui-palette-warning-mainChannel) / 0.38)',
        },
        info: {
          main: '#06B6D4',
          light: '#67E8F9',
          dark: '#0891B2',
          contrastText: '#FFF',
          lighterOpacity: 'rgb(var(--mui-palette-info-mainChannel) / 0.08)',
          lightOpacity: 'rgb(var(--mui-palette-info-mainChannel) / 0.16)',
          mainOpacity: 'rgb(var(--mui-palette-info-mainChannel) / 0.24)',
          darkOpacity: 'rgb(var(--mui-palette-info-mainChannel) / 0.32)',
          darkerOpacity: 'rgb(var(--mui-palette-info-mainChannel) / 0.38)',
        },
        success: {
          main: '#10B981',
          light: '#6EE7B7',
          dark: '#059669',
          contrastText: '#FFF',
          lighterOpacity: 'rgb(var(--mui-palette-success-mainChannel) / 0.08)',
          lightOpacity: 'rgb(var(--mui-palette-success-mainChannel) / 0.16)',
          mainOpacity: 'rgb(var(--mui-palette-success-mainChannel) / 0.24)',
          darkOpacity: 'rgb(var(--mui-palette-success-mainChannel) / 0.32)',
          darkerOpacity: 'rgb(var(--mui-palette-success-mainChannel) / 0.38)',
        },
        text: {
          primary: 'rgba(15, 23, 42, 0.9)',
          secondary: 'rgba(15, 23, 42, 0.6)',
          disabled: 'rgba(15, 23, 42, 0.38)',
          primaryChannel: '15 23 42',
          secondaryChannel: '15 23 42',
        },
        divider: 'rgba(15, 23, 42, 0.08)',
        dividerChannel: '15 23 42',
        background: {
          default: skin === 'bordered' ? '#FFFFFF' : '#FCFCFB',
          paper: '#FFFFFF',
          paperChannel: '255 255 255',
        },
        action: {
          active: 'rgba(15, 23, 42, 0.54)',
          hover: 'rgba(15, 23, 42, 0.04)',
          selected: 'rgba(15, 23, 42, 0.06)',
          disabled: 'rgba(15, 23, 42, 0.26)',
          disabledBackground: 'rgba(15, 23, 42, 0.12)',
          focus: 'rgba(15, 23, 42, 0.08)',
          focusOpacity: 0.08,
          activeChannel: '15 23 42',
          selectedChannel: '15 23 42',
        },
        Alert: {
          errorColor: 'var(--mui-palette-error-main)',
          warningColor: 'var(--mui-palette-warning-main)',
          infoColor: 'var(--mui-palette-info-main)',
          successColor: 'var(--mui-palette-success-main)',
          errorStandardBg: 'var(--mui-palette-error-lightOpacity)',
          warningStandardBg: 'var(--mui-palette-warning-lightOpacity)',
          infoStandardBg: 'var(--mui-palette-info-lightOpacity)',
          successStandardBg: 'var(--mui-palette-success-lightOpacity)',
          errorFilledColor: 'var(--mui-palette-error-contrastText)',
          warningFilledColor: 'var(--mui-palette-warning-contrastText)',
          infoFilledColor: 'var(--mui-palette-info-contrastText)',
          successFilledColor: 'var(--mui-palette-success-contrastText)',
          errorFilledBg: 'var(--mui-palette-error-main)',
          warningFilledBg: 'var(--mui-palette-warning-main)',
          infoFilledBg: 'var(--mui-palette-info-main)',
          successFilledBg: 'var(--mui-palette-success-main)',
        },
        Avatar: {
          defaultBg: '#F1F5F9',
        },
        Chip: {
          defaultBorder: 'var(--mui-palette-divider)',
        },
        FilledInput: {
          bg: 'var(--mui-palette-action-hover)',
          hoverBg: 'var(--mui-palette-action-selected)',
          disabledBg: 'var(--mui-palette-action-hover)',
        },
        SnackbarContent: {
          bg: '#1E293B',
          color: '#F8FAFC',
        },
        Switch: {
          defaultColor: 'var(--mui-palette-common-white)',
          defaultDisabledColor: 'var(--mui-palette-common-white)',
          primaryDisabledColor: 'var(--mui-palette-common-white)',
          secondaryDisabledColor: 'var(--mui-palette-common-white)',
          errorDisabledColor: 'var(--mui-palette-common-white)',
          warningDisabledColor: 'var(--mui-palette-common-white)',
          infoDisabledColor: 'var(--mui-palette-common-white)',
          successDisabledColor: 'var(--mui-palette-common-white)',
        },
        Tooltip: {
          bg: '#1E293B',
        },
        TableCell: {
          border: 'var(--mui-palette-divider)',
        },
        customColors: {
          bodyBg: '#FCFCFB',
          chatBg: '#F5F5F5',
          greyLightBg: '#FCFCFB',
          inputBorder: 'rgba(15, 23, 42, 0.16)',
          tableHeaderBg: '#F8FAFC',
          tooltipText: '#F8FAFC',
          trackBg: '#E2E8F0',
        },
      },
    },
    dark: {
      palette: {
        primary: {
          main: '#F43F5E',
          light: '#FB7185',
          dark: '#E11D48',
          contrastText: '#FFF',
          lighterOpacity: 'rgb(var(--mui-palette-primary-mainChannel) / 0.08)',
          lightOpacity: 'rgb(var(--mui-palette-primary-mainChannel) / 0.16)',
          mainOpacity: 'rgb(var(--mui-palette-primary-mainChannel) / 0.24)',
          darkOpacity: 'rgb(var(--mui-palette-primary-mainChannel) / 0.32)',
          darkerOpacity: 'rgb(var(--mui-palette-primary-mainChannel) / 0.38)',
        },
        secondary: {
          main: '#94A3B8',
          light: '#CBD5E1',
          dark: '#64748B',
          contrastText: '#FFF',
          lighterOpacity: 'rgb(var(--mui-palette-secondary-mainChannel) / 0.08)',
          lightOpacity: 'rgb(var(--mui-palette-secondary-mainChannel) / 0.16)',
          mainOpacity: 'rgb(var(--mui-palette-secondary-mainChannel) / 0.24)',
          darkOpacity: 'rgb(var(--mui-palette-secondary-mainChannel) / 0.32)',
          darkerOpacity: 'rgb(var(--mui-palette-secondary-mainChannel) / 0.38)',
        },
        error: {
          main: '#F87171',
          light: '#FCA5A5',
          dark: '#EF4444',
          contrastText: '#FFF',
          lighterOpacity: 'rgb(var(--mui-palette-error-mainChannel) / 0.08)',
          lightOpacity: 'rgb(var(--mui-palette-error-mainChannel) / 0.16)',
          mainOpacity: 'rgb(var(--mui-palette-error-mainChannel) / 0.24)',
          darkOpacity: 'rgb(var(--mui-palette-error-mainChannel) / 0.32)',
          darkerOpacity: 'rgb(var(--mui-palette-error-mainChannel) / 0.38)',
        },
        warning: {
          main: '#FBBF24',
          light: '#FCD34D',
          dark: '#F59E0B',
          contrastText: '#000',
          lighterOpacity: 'rgb(var(--mui-palette-warning-mainChannel) / 0.08)',
          lightOpacity: 'rgb(var(--mui-palette-warning-mainChannel) / 0.16)',
          mainOpacity: 'rgb(var(--mui-palette-warning-mainChannel) / 0.24)',
          darkOpacity: 'rgb(var(--mui-palette-warning-mainChannel) / 0.32)',
          darkerOpacity: 'rgb(var(--mui-palette-warning-mainChannel) / 0.38)',
        },
        info: {
          main: '#22D3EE',
          light: '#67E8F9',
          dark: '#06B6D4',
          contrastText: '#000',
          lighterOpacity: 'rgb(var(--mui-palette-info-mainChannel) / 0.08)',
          lightOpacity: 'rgb(var(--mui-palette-info-mainChannel) / 0.16)',
          mainOpacity: 'rgb(var(--mui-palette-info-mainChannel) / 0.24)',
          darkOpacity: 'rgb(var(--mui-palette-info-mainChannel) / 0.32)',
          darkerOpacity: 'rgb(var(--mui-palette-info-mainChannel) / 0.38)',
        },
        success: {
          main: '#34D399',
          light: '#6EE7B7',
          dark: '#10B981',
          contrastText: '#000',
          lighterOpacity: 'rgb(var(--mui-palette-success-mainChannel) / 0.08)',
          lightOpacity: 'rgb(var(--mui-palette-success-mainChannel) / 0.16)',
          mainOpacity: 'rgb(var(--mui-palette-success-mainChannel) / 0.24)',
          darkOpacity: 'rgb(var(--mui-palette-success-mainChannel) / 0.32)',
          darkerOpacity: 'rgb(var(--mui-palette-success-mainChannel) / 0.38)',
        },
        text: {
          primary: 'rgba(226, 232, 240, 0.93)',
          secondary: 'rgba(226, 232, 240, 0.68)',
          disabled: 'rgba(226, 232, 240, 0.38)',
          primaryChannel: '226 232 240',
          secondaryChannel: '226 232 240',
        },
        divider: 'rgba(226, 232, 240, 0.08)',
        dividerChannel: '226 232 240',
        background: {
          default: skin === 'bordered' ? '#12121A' : '#0A0A0F',
          paper: '#12121A',
          paperChannel: '18 18 26',
        },
        action: {
          active: 'rgba(226, 232, 240, 0.54)',
          hover: 'rgba(226, 232, 240, 0.06)',
          selected: 'rgba(226, 232, 240, 0.08)',
          disabled: 'rgba(226, 232, 240, 0.26)',
          disabledBackground: 'rgba(226, 232, 240, 0.12)',
          focus: 'rgba(226, 232, 240, 0.08)',
          focusOpacity: 0.08,
          activeChannel: '226 232 240',
          selectedChannel: '226 232 240',
        },
        Alert: {
          errorColor: 'var(--mui-palette-error-main)',
          warningColor: 'var(--mui-palette-warning-main)',
          infoColor: 'var(--mui-palette-info-main)',
          successColor: 'var(--mui-palette-success-main)',
          errorStandardBg: 'var(--mui-palette-error-lightOpacity)',
          warningStandardBg: 'var(--mui-palette-warning-lightOpacity)',
          infoStandardBg: 'var(--mui-palette-info-lightOpacity)',
          successStandardBg: 'var(--mui-palette-success-lightOpacity)',
          errorFilledColor: 'var(--mui-palette-error-contrastText)',
          warningFilledColor: 'var(--mui-palette-warning-contrastText)',
          infoFilledColor: 'var(--mui-palette-info-contrastText)',
          successFilledColor: 'var(--mui-palette-success-contrastText)',
          errorFilledBg: 'var(--mui-palette-error-main)',
          warningFilledBg: 'var(--mui-palette-warning-main)',
          infoFilledBg: 'var(--mui-palette-info-main)',
          successFilledBg: 'var(--mui-palette-success-main)',
        },
        Avatar: {
          defaultBg: '#1E1E2E',
        },
        Chip: {
          defaultBorder: 'var(--mui-palette-divider)',
        },
        FilledInput: {
          bg: 'var(--mui-palette-action-hover)',
          hoverBg: 'var(--mui-palette-action-selected)',
          disabledBg: `var(--mui-palette-action-hover)`,
        },
        SnackbarContent: {
          bg: '#1A1A2E',
          color: '#E2E8F0',
        },
        Switch: {
          defaultColor: 'var(--mui-palette-common-white)',
          defaultDisabledColor: 'var(--mui-palette-common-white)',
          primaryDisabledColor: 'var(--mui-palette-common-white)',
          secondaryDisabledColor: 'var(--mui-palette-common-white)',
          errorDisabledColor: 'var(--mui-palette-common-white)',
          warningDisabledColor: 'var(--mui-palette-common-white)',
          infoDisabledColor: 'var(--mui-palette-common-white)',
          successDisabledColor: 'var(--mui-palette-common-white)',
        },
        Tooltip: {
          bg: '#1A1A2E',
        },
        TableCell: {
          border: 'var(--mui-palette-divider)',
        },
        customColors: {
          bodyBg: '#0A0A0F',
          chatBg: '#0E0E16',
          greyLightBg: '#1A1A2E',
          inputBorder: 'rgba(226, 232, 240, 0.12)',
          tableHeaderBg: '#12121A',
          tooltipText: '#E2E8F0',
          trackBg: '#1E1E2E',
        },
      },
    },
  } as ColorSchemes;
};

export default colorSchemes;
